// src/services/automation.service.ts
// Service for managing automation rules and their execution

import { db } from '../config/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  limit as firestoreLimit
} from 'firebase/firestore';
import {
  AutomationRule,
  AutomationExecution,
  CreateAutomationRuleDto,
  UpdateAutomationRuleDto,
  AutomationTemplate,
  ExecutionStats,
  AutomationCondition,
  AutomationAction
} from '../types/automation.types';
import toast from 'react-hot-toast';


export const AUTOMATION_TEMPLATES: AutomationTemplate[] = [
  {
    id: 'lead_with_appointment_task',
    name: 'Lead with Appointment - Client Task',
    description: 'When an appointment is created, create a task for the client_user to confirm the appointment',
    category: 'appointment',
    trigger: { type: 'event', event: 'appointment_created' },
    conditions: [],
    actions: [{
      type: 'create_task',
      taskConfig: {
        type: 'lead_followup',
        titleTemplate: 'Confirm appointment with {{lead.parentName}}',
        descriptionTemplate: 'New appointment scheduled for {{appointment.date}} at {{appointment.startTime}}. Please contact the family to confirm attendance and answer any questions.',
        priority: 'high',
        dueDaysFromTrigger: 1,
        dueTime: '17:00'
      },
      assignTo: { type: 'assigned_user' }
    }],
    configurableFields: [
      {
        path: 'actions.0.taskConfig.dueDaysFromTrigger',
        label: 'Due Days After Appointment Created',
        type: 'number',
        defaultValue: 1,
        description: 'Number of days after appointment creation for task to be due'
      },
      {
        path: 'actions.0.taskConfig.priority',
        label: 'Task Priority',
        type: 'select',
        defaultValue: 'high',
        options: [
          { label: 'Low', value: 'low' },
          { label: 'Medium', value: 'medium' },
          { label: 'High', value: 'high' },
          { label: 'Urgent', value: 'urgent' }
        ]
      }
    ]
  },
  {
    id: 'day_before_appointment_reminder',
    name: 'Day Before Appointment Reminder',
    description: 'Create a task for the field user to contact the lead the day before their appointment',
    category: 'appointment',
    trigger: {
      type: 'scheduled',
      schedule: {
        type: 'relative',
        relativeTo: 'appointment_date',
        offsetDays: -1,
        time: '09:00',
        timezone: 'America/New_York'
      }
    },
    conditions: [{ field: 'appointment.status', operator: 'equals', value: 'scheduled' }],
    actions: [{
      type: 'create_task',
      taskConfig: {
        type: 'tour_reminder',
        titleTemplate: 'Reminder: {{appointment.eventType}} with {{lead.parentName}} tomorrow',
        descriptionTemplate: 'Appointment scheduled for tomorrow ({{appointment.date}} at {{appointment.startTime}}). Please call to confirm attendance and ask if they need any support or have questions.',
        priority: 'high',
        dueDaysFromTrigger: 0,
        dueTime: '17:00'
      },
      assignTo: { type: 'captured_by' }
    }],
    configurableFields: [
      {
        path: 'trigger.schedule.time',
        label: 'Reminder Time',
        type: 'time',
        defaultValue: '09:00',
        description: 'Time of day to create the reminder task'
      },
      {
        path: 'actions.0.assignTo.type',
        label: 'Assign To',
        type: 'select',
        defaultValue: 'captured_by',
        options: [
          { label: 'Field user who captured lead', value: 'captured_by' },
          { label: 'Assigned client user', value: 'assigned_user' },
          { label: 'Client users (by role)', value: 'client_user' }
        ]
      }
    ]
  },
  {
    id: 'appointment_noshow_alert',
    name: 'Appointment No-Show Alert',
    description: 'Notify field user and provider when an appointment is marked as no-show to reschedule',
    category: 'alert',
    trigger: { type: 'event', event: 'appointment_status_changed' },
    conditions: [{ field: 'appointment.status', operator: 'equals', value: 'no-show' }],
    actions: [
      {
        type: 'send_notification',
        notificationConfig: {
          titleTemplate: 'No-Show: {{lead.parentName}}',
          messageTemplate: 'The {{appointment.eventType}} scheduled for {{appointment.date}} was marked as no-show. Please contact the family to reschedule.',
          channels: ['in_app', 'email']
        },
        assignTo: { type: 'captured_by' }
      },
      {
        type: 'send_notification',
        notificationConfig: {
          titleTemplate: 'No-Show Alert: {{lead.parentName}}',
          messageTemplate: 'Appointment no-show for {{lead.parentName}} ({{appointment.date}}). Field user has been notified to reschedule.',
          channels: ['in_app']
        },
        assignTo: { type: 'role', role: ['provider_user', 'provider_admin'] }
      }
    ],
    configurableFields: []
  },
  {
    id: 'lead_not_contacted_alert',
    name: 'Lead Not Contacted Alert',
    description: 'Daily check to alert provider users when leads have not been contacted within a specified number of days',
    category: 'alert',
    trigger: {
      type: 'scheduled',
      schedule: {
        type: 'fixed',
        time: '10:00',
        timezone: 'America/New_York'
      }
    },
    conditions: [
      { field: 'lead.status', operator: 'equals', value: 'uncontacted' },
      { field: 'daysSince.leadCreated', operator: 'greater_than', value: 2 }
    ],
    actions: [{
      type: 'create_task',
      taskConfig: {
        type: 'lead_no_contact',
        titleTemplate: 'Lead Not Contacted: {{lead.parentName}}',
        descriptionTemplate: 'Alert: {{lead.parentName}} has not been contacted in {{daysSince.leadCreated}} days. Please follow up immediately.',
        priority: 'high',
        dueDaysFromTrigger: 0,
        dueTime: '17:00'
      },
      assignTo: { type: 'role', role: 'provider_user' }
    }],
    configurableFields: [
      {
        path: 'conditions.1.value',
        label: 'Days Without Contact',
        type: 'number',
        defaultValue: 2,
        required: true,
        description: 'Number of days without contact before alerting'
      },
      {
        path: 'trigger.schedule.time',
        label: 'Daily Check Time',
        type: 'time',
        defaultValue: '10:00',
        description: 'Time of day to run the check'
      }
    ]
  }
];


class AutomationService {
  private readonly collectionName = 'automationRules';
  private readonly executionsCollectionName = 'automationExecutions';

  async createRule(data: CreateAutomationRuleDto, userId: string): Promise<string> {
    try {
      const ruleRef = doc(collection(db, this.collectionName));

      // Add IDs to conditions and actions
      const conditions: AutomationCondition[] = data.conditions.map((c, i) => ({
        ...c,
        id: `cond_${Date.now()}_${i}`
      }));

      const actions: AutomationAction[] = data.actions.map((a, i) => ({
        ...a,
        id: `action_${Date.now()}_${i}`
      }));

      const rule: AutomationRule = {
        id: ruleRef.id,
        organizationId: data.organizationId,
        name: data.name,
        description: data.description,
        enabled: data.enabled ?? true,
        isTemplate: false,
        templateId: data.templateId,
        trigger: data.trigger,
        conditions,
        conditionLogic: data.conditionLogic || 'and',
        actions,
        projectScope: data.projectScope,
        projectIds: data.projectIds,
        executionStats: {
          totalExecutions: 0,
          successfulExecutions: 0,
          failedExecutions: 0
        },
        createdBy: userId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        updatedBy: userId
      };

      await setDoc(ruleRef, rule);
      toast.success('Automation rule created');
      return ruleRef.id;
    } catch (error) {
      console.error('Error creating automation rule:', error);
      toast.error('Failed to create automation rule');
      throw error;
    }
  }

  async getRule(ruleId: string): Promise<AutomationRule | null> {
    try {
      const docRef = doc(db, this.collectionName, ruleId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists() ? (docSnap.data() as AutomationRule) : null;
    } catch (error) {
      console.error('Error fetching automation rule:', error);
      return null;
    }
  }

  async getRules(filters?: {
    enabled?: boolean;
    projectId?: string;
    triggerEvent?: string;
  }): Promise<AutomationRule[]> {
    try {
      let q = query(collection(db, this.collectionName), orderBy('createdAt', 'desc'));

      const snapshot = await getDocs(q);
      let rules = snapshot.docs.map(doc => doc.data() as AutomationRule);

      // Apply filters in memory (Firestore limitations with multiple where clauses)
      if (filters?.enabled !== undefined) {
        rules = rules.filter(r => r.enabled === filters.enabled);
      }
      if (filters?.projectId) {
        rules = rules.filter(r =>
          r.projectScope === 'all' ||
          (r.projectIds && r.projectIds.includes(filters.projectId!))
        );
      }
      if (filters?.triggerEvent) {
        rules = rules.filter(r => r.trigger.event === filters.triggerEvent);
      }

      return rules;
    } catch (error) {
      console.error('Error fetching automation rules:', error);
      return [];
    }
  }

  async updateRule(ruleId: string, updates: UpdateAutomationRuleDto, userId: string): Promise<void> {
    try {
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy: userId
      };

      // Add IDs to new conditions/actions if provided
      if (updates.conditions) {
        updateData.conditions = updates.conditions.map((c, i) => ({
          ...c,
          id: `cond_${Date.now()}_${i}`
        }));
      }
      if (updates.actions) {
        updateData.actions = updates.actions.map((a, i) => ({
          ...a,
          id: `action_${Date.now()}_${i}`
        }));
      }

      await updateDoc(doc(db, this.collectionName, ruleId), updateData);
      toast.success('Automation rule updated');
    } catch (error) {
      console.error('Error updating automation rule:', error);
      toast.error('Failed to update automation rule');
      throw error;
    }
  }

  async deleteRule(ruleId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.collectionName, ruleId));
      toast.success('Automation rule deleted');
    } catch (error) {
      console.error('Error deleting automation rule:', error);
      toast.error('Failed to delete automation rule');
      throw error;
    }
  }

  async toggleRule(ruleId: string, enabled: boolean, userId: string): Promise<void> {
    try {
      await updateDoc(doc(db, this.collectionName, ruleId), {
        enabled,
        updatedAt: Timestamp.now(),
        updatedBy: userId
      });
      toast.success(`Rule ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error toggling automation rule:', error);
      toast.error('Failed to update rule status');
      throw error;
    }
  }

  getTemplates(): AutomationTemplate[] {
    return AUTOMATION_TEMPLATES;
  }

  getTemplateById(templateId: string): AutomationTemplate | undefined {
    return AUTOMATION_TEMPLATES.find(t => t.id === templateId);
  }

  async createRuleFromTemplate(
    templateId: string,
    config: Record<string, any>,
    userId: string
  ): Promise<string> {
    const template = this.getTemplateById(templateId);
    if (!template) {
      throw new Error('Template not found');
    }

    // Deep clone template data
    const ruleData: CreateAutomationRuleDto = {
      name: config.name || template.name,
      description: config.description || template.description,
      enabled: config.enabled ?? true,
      trigger: JSON.parse(JSON.stringify(template.trigger)),
      conditions: JSON.parse(JSON.stringify(template.conditions)),
      conditionLogic: 'and',
      actions: JSON.parse(JSON.stringify(template.actions)),
      projectScope: config.projectScope || 'all',
      projectIds: config.projectIds,
      templateId
    };

    // Apply configurable field values
    template.configurableFields.forEach(field => {
      const value = config[field.path] ?? field.defaultValue;
      this.setNestedValue(ruleData, field.path, value);
    });

    return this.createRule(ruleData, userId);
  }

  async logExecution(execution: Omit<AutomationExecution, 'id'>): Promise<string> {
    try {
      const execRef = doc(collection(db, this.executionsCollectionName));
      const execData: AutomationExecution = {
        ...execution,
        id: execRef.id
      };
      await setDoc(execRef, execData);
      return execRef.id;
    } catch (error) {
      console.error('Error logging execution:', error);
      throw error;
    }
  }

  async getExecutions(options?: {
    ruleId?: string;
    limit?: number;
    status?: 'success' | 'failed' | 'partial';
  }): Promise<AutomationExecution[]> {
    try {
      let q = query(
        collection(db, this.executionsCollectionName),
        orderBy('executedAt', 'desc'),
        firestoreLimit(options?.limit || 50)
      );

      const snapshot = await getDocs(q);
      let executions = snapshot.docs.map(doc => doc.data() as AutomationExecution);

      // Filter in memory
      if (options?.ruleId) {
        executions = executions.filter(e => e.ruleId === options.ruleId);
      }
      if (options?.status) {
        executions = executions.filter(e => e.status === options.status);
      }

      return executions;
    } catch (error) {
      console.error('Error fetching executions:', error);
      return [];
    }
  }

  async updateExecutionStats(ruleId: string, success: boolean): Promise<void> {
    try {
      const rule = await this.getRule(ruleId);
      if (!rule) return;

      const stats: ExecutionStats = {
        totalExecutions: (rule.executionStats?.totalExecutions || 0) + 1,
        successfulExecutions: (rule.executionStats?.successfulExecutions || 0) + (success ? 1 : 0),
        failedExecutions: (rule.executionStats?.failedExecutions || 0) + (success ? 0 : 1),
        lastExecutedAt: Timestamp.now()
      };

      await updateDoc(doc(db, this.collectionName, ruleId), { executionStats: stats });
    } catch (error) {
      console.error('Error updating execution stats:', error);
    }
  }

  subscribeToRules(callback: (rules: AutomationRule[]) => void): () => void {
    const q = query(collection(db, this.collectionName), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snapshot) => {
      const rules = snapshot.docs.map(doc => doc.data() as AutomationRule);
      callback(rules);
    }, (error) => {
      console.error('Error in rules subscription:', error);
      callback([]);
    });
  }

  subscribeToExecutions(
    callback: (executions: AutomationExecution[]) => void,
    limit: number = 20
  ): () => void {
    const q = query(
      collection(db, this.executionsCollectionName),
      orderBy('executedAt', 'desc'),
      firestoreLimit(limit)
    );
    return onSnapshot(q, (snapshot) => {
      const executions = snapshot.docs.map(doc => doc.data() as AutomationExecution);
      callback(executions);
    }, (error) => {
      console.error('Error in executions subscription:', error);
      callback([]);
    });
  }

  private setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i];
      const nextKey = keys[i + 1];

      // Check if next key is a number (array index)
      if (!isNaN(Number(nextKey))) {
        current[key] = current[key] || [];
      } else {
        current[key] = current[key] || {};
      }
      current = current[key];
    }

    current[keys[keys.length - 1]] = value;
  }

  // Template variable replacement helper (used by Cloud Functions)
  static processTemplate(template: string, data: Record<string, any>): string {
    return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
      const value = AutomationService.getNestedValue(data, path.trim());
      return value !== undefined && value !== null ? String(value) : match;
    });
  }

  static getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }
}

export const automationService = new AutomationService();
